import { motion } from "framer-motion";
import { Package, FileLock2, Pill, Sparkles } from "lucide-react";
import { SERVICES } from "@/constants/testIds";

const services = [
  {
    slug: "retail",
    title: "Small retail parcels",
    body: "Same-day fulfillment for boutique shops, florists, bakeries — any small business that needs a package delivered across town.",
    Icon: Package,
  },
  {
    slug: "private-courier",
    title: "Private courier",
    body: "Discreet, hand-carried delivery for residential and commercial addresses. Contracts, keys, samples, small equipment.",
    Icon: FileLock2,
  },
  {
    slug: "medical",
    title: "Medical & pharmaceutical",
    body: "HIPAA-aware drivers with OSHA training for specimens, prescriptions and clinical supplies — chain of custody preserved.",
    Icon: Pill,
  },
  {
    slug: "more",
    title: "More to come",
    body: "Refrigerated routes, catalog fulfillment, and dedicated route contracts are next on the map. Ask what we can build for you.",
    Icon: Sparkles,
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid={SERVICES.root}
      className="bg-cream/60 py-24 md:py-32 relative overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -right-24 top-10 font-serif text-[18vw] leading-none text-forest/5 select-none pointer-events-none"
      >
        service.
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-10 mb-14">
          <div className="col-span-12 md:col-span-8">
            <div className="text-xs uppercase tracking-[0.3em] text-terracotta mb-4">
              Services
            </div>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] text-forest tracking-tight">
              Purpose-built routes for <span className="italic font-light">small business</span>.
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-10">
          {/* Services grid */}
          <div className="col-span-12 lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-4">
            {services.map((s, i) => (
              <motion.article
                key={s.slug}
                data-testid={SERVICES.card(s.slug)}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                className="group p-6 md:p-8 bg-paper border border-forest/15 hover:border-terracotta transition-colors relative"
              >
                <div className="flex items-center justify-between">
                  <s.Icon className="w-6 h-6 text-forest group-hover:text-terracotta transition-colors" strokeWidth={1.5} />
                  <span className="font-mono text-xs text-charcoal/40">0{i + 1}</span>
                </div>
                <h3 className="mt-6 font-serif text-2xl text-forest leading-tight">
                  {s.title}
                </h3>
                <p className="mt-3 text-sm md:text-base text-charcoal/70 leading-relaxed">
                  {s.body}
                </p>
              </motion.article>
            ))}
          </div>

          {/* Right: driver photo + call-to-action */}
          <div className="col-span-12 lg:col-span-5 flex flex-col gap-6">
            <div className="relative frame-hover">
              <img
                src="/services-driver.jpg"
                alt="A Waylio courier in a white SUV preparing to deliver a shipment"
                className="w-full h-[360px] md:h-[440px] object-cover"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-forest/10" />
            </div>
            <div className="bg-forest text-cream p-6 md:p-8">
              <div className="text-[10px] uppercase tracking-[0.3em] text-cream/60">Ready? Set. Go!</div>
              <div className="mt-3 font-serif text-2xl md:text-3xl leading-tight">
                Tell us what you'd like <span className="italic font-light text-terracotta">delivered</span>.
              </div>
              <a
                href="/quote"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-terracotta hover:bg-terracotta-hover text-cream px-5 py-2.5 text-sm font-medium transition-colors"
              >
                Get a Quote
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
